/**
 * Attempt Repository
 *
 * Handles all database operations for quiz and exam attempts,
 * including saving results and retrieving history for review.
 */

import { supabase } from '../lib/supabase';
import {
  createBaseRepository,
  handleSupabaseError,
} from './baseRepository';

/**
 * Quiz/Exam Attempt Repository
 */
export const attemptRepository = {
  ...createBaseRepository('quiz_exam_attempts'),

  /**
   * Save a new attempt
   * @param {Object} attemptData - Attempt data
   * @returns {Promise<Object>} Created attempt record
   */
  async saveAttempt(attemptData) {
    const { data, error } = await supabase
      .from('quiz_exam_attempts')
      .insert({
        auth0_id: attemptData.auth0_id,
        content_type: attemptData.content_type,
        content_id: attemptData.content_id,
        content_title: attemptData.content_title,
        mode: attemptData.mode,
        score: attemptData.score,
        total_questions: attemptData.total_questions,
        percentage: attemptData.percentage,
        is_adaptive: attemptData.is_adaptive || false,
        adaptive_result: attemptData.adaptive_result || null,
        adaptive_theta: attemptData.adaptive_theta || null,
        adaptive_confidence: attemptData.adaptive_confidence || null,
        is_ngn: attemptData.is_ngn || false,
        answers_data: attemptData.answers_data,
        questions_administered: attemptData.questions_administered || null,
        time_spent_seconds: attemptData.time_spent_seconds || 0,
        completed_at: attemptData.completed_at || new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      throw handleSupabaseError(error, 'quiz_exam_attempts.saveAttempt');
    }

    return data;
  },

  /**
   * Get attempt by ID
   * @param {string} attemptId - The attempt ID
   * @returns {Promise<Object|null>} Attempt record or null
   */
  async getAttemptById(attemptId) {
    const { data, error } = await supabase
      .from('quiz_exam_attempts')
      .select('*')
      .eq('id', attemptId)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw handleSupabaseError(error, 'quiz_exam_attempts.getAttemptById');
    }

    return data || null;
  },

  /**
   * Get all attempts for a user
   * @param {string} auth0Id - The user's Auth0 ID
   * @param {string} contentType - Optional content type filter ('quiz' or 'exam')
   * @returns {Promise<Array>} List of attempts
   */
  async getAttemptsByUser(auth0Id, contentType = null) {
    let query = supabase
      .from('quiz_exam_attempts')
      .select('*')
      .eq('auth0_id', auth0Id)
      .order('completed_at', { ascending: false });

    if (contentType) {
      query = query.eq('content_type', contentType);
    }

    const { data, error } = await query;

    if (error) {
      throw handleSupabaseError(error, 'quiz_exam_attempts.getAttemptsByUser');
    }

    return data || [];
  },

  /**
   * Get attempts for specific content
   * @param {string} auth0Id - The user's Auth0 ID
   * @param {string} contentType - Content type ('quiz' or 'exam')
   * @param {string} contentId - Content ID
   * @returns {Promise<Array>} List of attempts for this content
   */
  async getAttemptsByContent(auth0Id, contentType, contentId) {
    const { data, error } = await supabase
      .from('quiz_exam_attempts')
      .select('*')
      .eq('auth0_id', auth0Id)
      .eq('content_type', contentType)
      .eq('content_id', contentId)
      .order('completed_at', { ascending: false });

    if (error) {
      throw handleSupabaseError(error, 'quiz_exam_attempts.getAttemptsByContent');
    }

    return data || [];
  },

  /**
   * Get the latest attempt for specific content
   * @param {string} auth0Id - The user's Auth0 ID
   * @param {string} contentType - Content type ('quiz' or 'exam')
   * @param {string} contentId - Content ID
   * @returns {Promise<Object|null>} Latest attempt or null
   */
  async getLatestAttempt(auth0Id, contentType, contentId) {
    const { data, error } = await supabase
      .from('quiz_exam_attempts')
      .select('*')
      .eq('auth0_id', auth0Id)
      .eq('content_type', contentType)
      .eq('content_id', contentId)
      .order('completed_at', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw handleSupabaseError(error, 'quiz_exam_attempts.getLatestAttempt');
    }

    return data || null;
  },

  /**
   * Get combined history for a user (both quizzes and exams)
   * @param {string} auth0Id - The user's Auth0 ID
   * @param {Object} options - Query options
   * @param {number} options.limit - Max results (default 100)
   * @param {number} options.offset - Skip results (default 0)
   * @returns {Promise<Array>} Combined history sorted by date
   */
  async getAllHistory(auth0Id, options = {}) {
    const { limit = 100, offset = 0 } = options;

    const { data, error } = await supabase
      .from('quiz_exam_attempts')
      .select('*')
      .eq('auth0_id', auth0Id)
      .order('completed_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      throw handleSupabaseError(error, 'quiz_exam_attempts.getAllHistory');
    }

    return data || [];
  },

  /**
   * Get completion status for multiple content items
   * Returns a map of contentId -> latest attempt for quick lookup
   * @param {string} auth0Id - The user's Auth0 ID
   * @param {string} contentType - Content type ('quiz' or 'exam')
   * @param {Array<string>} contentIds - Array of content IDs to check
   * @returns {Promise<Object>} Map of contentId -> latest attempt
   */
  async getCompletionStatus(auth0Id, contentType, contentIds) {
    if (!contentIds || contentIds.length === 0) {
      return {};
    }

    const { data, error } = await supabase
      .from('quiz_exam_attempts')
      .select('*')
      .eq('auth0_id', auth0Id)
      .eq('content_type', contentType)
      .in('content_id', contentIds)
      .order('completed_at', { ascending: false });

    if (error) {
      throw handleSupabaseError(error, 'quiz_exam_attempts.getCompletionStatus');
    }

    // Build map with only the latest attempt per content
    const statusMap = {};
    (data || []).forEach((attempt) => {
      if (!statusMap[attempt.content_id]) {
        statusMap[attempt.content_id] = attempt;
      }
    });

    return statusMap;
  },

  /**
   * Get history statistics for a user
   * @param {string} auth0Id - The user's Auth0 ID
   * @returns {Promise<Object>} Statistics object
   */
  async getHistoryStats(auth0Id) {
    const { data, error } = await supabase
      .from('quiz_exam_attempts')
      .select('content_type, percentage')
      .eq('auth0_id', auth0Id);

    if (error) {
      throw handleSupabaseError(error, 'quiz_exam_attempts.getHistoryStats');
    }

    const stats = {
      totalAttempts: 0,
      examsCompleted: 0,
      quizzesCompleted: 0,
      averageScore: 0,
    };

    if (!data || data.length === 0) {
      return stats;
    }

    let totalPercentage = 0;
    data.forEach((attempt) => {
      stats.totalAttempts++;
      if (attempt.content_type === 'exam') {
        stats.examsCompleted++;
      } else {
        stats.quizzesCompleted++;
      }
      totalPercentage += attempt.percentage || 0;
    });

    stats.averageScore = Math.round(totalPercentage / stats.totalAttempts);

    return stats;
  },

  /**
   * Delete an attempt (for reset functionality)
   * @param {string} attemptId - The attempt ID
   * @param {string} auth0Id - The user's Auth0 ID (for security)
   * @returns {Promise<boolean>} True if deleted
   */
  async deleteAttempt(attemptId, auth0Id) {
    const { error } = await supabase
      .from('quiz_exam_attempts')
      .delete()
      .eq('id', attemptId)
      .eq('auth0_id', auth0Id);

    if (error) {
      throw handleSupabaseError(error, 'quiz_exam_attempts.deleteAttempt');
    }

    return true;
  },

  /**
   * Delete all attempts for specific content (full reset)
   * @param {string} auth0Id - The user's Auth0 ID
   * @param {string} contentType - Content type ('quiz' or 'exam')
   * @param {string} contentId - Content ID
   * @returns {Promise<number>} Number of records deleted
   */
  async deleteAttemptsForContent(auth0Id, contentType, contentId) {
    const { data, error } = await supabase
      .from('quiz_exam_attempts')
      .delete()
      .eq('auth0_id', auth0Id)
      .eq('content_type', contentType)
      .eq('content_id', contentId)
      .select('id');

    if (error) {
      throw handleSupabaseError(error, 'quiz_exam_attempts.deleteAttemptsForContent');
    }

    return data?.length || 0;
  },
};

export default attemptRepository;
